import { useQuery } from '@tanstack/react-query'
import { useAuth } from '../contexts/AuthContext'
import { telasApi } from '../api/telas'
import { TelaResponse } from '../types/api'
import { useMemo } from 'react'
import { createLogger } from '../utils/logger'

const logger = createLogger('useMenu')

/**
 * Hook para buscar e processar menu de telas do backend
 * 
 * - Busca telas do tipo GALATEA_WEB
 * - Filtra por empresa do usuário (se aplicável)
 * - Ordena por posicao_menu
 * - Organiza hierarquia (parent_id)
 * - Filtra por permissões (backend já faz isso)
 */
export const useMenu = (
  tipoSistema: 'ADMIN' | 'GALATEA_WEB' = 'GALATEA_WEB',
  empresaId?: number | null
) => {
  const { user } = useAuth()

  // Buscar menu hierárquico do backend
  const { data: telas = [], isLoading, error } = useQuery<TelaResponse[]>({
    queryKey: ['menu', tipoSistema, user?.id, empresaId],
    queryFn: async () => {
      try {
        const result = await telasApi.getMenuHierarquico({
          tipo_sistema: tipoSistema,
          empresa_id: empresaId || undefined,
        })
        logger.debug(`Menu carregado: ${result.length} telas para ${tipoSistema}${empresaId ? ` (empresa: ${empresaId})` : ''}`)
        return result
      } catch (err: any) {
        logger.error('Erro ao carregar menu:', err)
        logger.error('Status:', err?.response?.status)
        logger.error('Detalhes:', err?.response?.data)
        throw err
      }
    },
    enabled: !!user, // Só buscar se houver usuário autenticado
    staleTime: 2 * 60 * 1000, // Cache 2 min - evita refetch excessivo
    gcTime: 5 * 60 * 1000, // Manter em cache 5 min
    refetchOnMount: true, // Buscar ao montar (usa cache se fresco)
    refetchOnWindowFocus: false, // Evitar refetch ao focar
    refetchOnReconnect: true,
    retry: 1,
  })

  // Processar telas em estrutura hierárquica
  const menuHierarquico = useMemo(() => {
    if (!Array.isArray(telas) || telas.length === 0) {
      return []
    }

    // FILTRO EXPLÍCITO: Excluir dicionários mesmo que estejam ativos (por segurança)
    const rotasExcluidas = ['/dicionario-dados', '/config/dicionario-dados', '/dicionariothor', '/config/dicionariothor']
    const nomesExcluidos = ['Dicionário de Dados', 'Dicionário Thor', 'Dicionario de Dados', 'Dicionario Thor']
    const telasFiltradas = telas.filter((tela: TelaResponse) => {
      if (!tela.ativo || tela.deleted_at) return false
      if (rotasExcluidas.includes(tela.rota)) return false
      if (nomesExcluidos.includes(tela.nome)) return false
      return true
    })

    // Ordenar por posicao_menu
    const telasOrdenadas = [...telasFiltradas].sort((a, b) => a.posicao_menu - b.posicao_menu)

    // Criar mapa de telas por ID para acesso rápido
    const telasMap = new Map<number, TelaResponse & { children: TelaResponse[] }>()
    telasOrdenadas.forEach((tela) => {
      telasMap.set(tela.id, { ...tela, children: [] })
    })

    // Construir hierarquia
    const raiz: (TelaResponse & { children: TelaResponse[] })[] = []

    telasOrdenadas.forEach((tela) => {
      const telaComChildren = telasMap.get(tela.id)!
      
      if (tela.parent_id && telasMap.has(tela.parent_id)) {
        // Adicionar como filho do pai
        const pai = telasMap.get(tela.parent_id)!
        pai.children.push(telaComChildren)
      } else {
        // É uma tela raiz
        raiz.push(telaComChildren)
      }
    })

    return raiz
  }, [telas])

  return {
    menuItems: menuHierarquico,
    isLoading,
    error,
    telas, // Telas brutas (sem hierarquia)
  }
}

